import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line, ComposedChart, Area } from "recharts";
import { formatCurrency, formatPercent, CHART_COLORS } from "@/lib/config";
import api from "@/lib/api";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="font-medium text-slate-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={entry.id || entry._id || index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="money-value">{formatCurrency(entry.value)}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ChangeIndicator = ({ current, previous }) => {
  if (!previous || previous === 0) return <Minus className="w-4 h-4 text-slate-400" />;
  const change = ((current - previous) / Math.abs(previous)) * 100;
  if (change > 0) {
    return (
      <span className="flex items-center gap-1 text-emerald-600 text-sm">
        <TrendingUp className="w-4 h-4" />
        +{change.toFixed(1)}%
      </span>
    );
  } else if (change < 0) {
    return (
      <span className="flex items-center gap-1 text-red-500 text-sm">
        <TrendingDown className="w-4 h-4" />
        {change.toFixed(1)}%
      </span>
    );
  }
  return <Minus className="w-4 h-4 text-slate-400" />;
};

export const Comparaison = () => {
  const [loading, setLoading] = useState(true);
  const [mensuelle2024, setMensuelle2024] = useState([]);
  const [mensuelle2025, setMensuelle2025] = useState([]);
  const [trimestrielle2024, setTrimestrielle2024] = useState([]);
  const [trimestrielle2025, setTrimestrielle2025] = useState([]);
  const [annuelle, setAnnuelle] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [m2024, m2025, t2024, t2025, yearly] = await Promise.all([
          api.getAnalyseMensuelle(2024),
          api.getAnalyseMensuelle(2025),
          api.getAnalyseTrimestrielle(2024),
          api.getAnalyseTrimestrielle(2025),
          api.getAnalyseAnnuelle()
        ]);
        setMensuelle2024(m2024);
        setMensuelle2025(m2025);
        setTrimestrielle2024(t2024);
        setTrimestrielle2025(t2025);
        setAnnuelle(yearly);
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  // Combiner données mensuelles pour comparaison
  const monthlyComparison = mensuelle2024.map((m, idx) => ({
    mois: m.mois_nom,
    revenus_2024: m.revenus,
    revenus_2025: mensuelle2025[idx]?.revenus || 0,
    profit_2024: m.profit,
    profit_2025: mensuelle2025[idx]?.profit || 0
  }));

  // Combiner données trimestrielles
  const quarterlyComparison = trimestrielle2024.map((t, idx) => ({
    trimestre: t.trimestre,
    revenus_2024: t.revenus,
    revenus_2025: trimestrielle2025[idx]?.revenus || 0,
    profit_2024: t.profit,
    profit_2025: trimestrielle2025[idx]?.profit || 0
  }));

  return (
    <div data-testid="comparaison-page" className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Comparaison</h1>
        <p className="text-slate-500 mt-1">Analyse MoM, QoQ et YoY</p>
      </div>

      <Tabs defaultValue="mom" className="animate-fade-in delay-100">
        <TabsList className="mb-6">
          <TabsTrigger value="mom">Mois vs Mois (MoM)</TabsTrigger>
          <TabsTrigger value="qoq">Trimestre vs Trimestre (QoQ)</TabsTrigger>
          <TabsTrigger value="yoy">Année vs Année (YoY)</TabsTrigger>
        </TabsList>

        {/* MoM */}
        <TabsContent value="mom">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Revenus Mensuels - 2024 vs 2025</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyComparison}>
                      <XAxis dataKey="mois" tick={{ fontSize: 10 }} tickFormatter={(v) => v.substring(0, 3)} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v/1000}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="revenus_2024" name="2024" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="revenus_2025" name="2025" fill={CHART_COLORS.revenus} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Détail Mensuel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 kpi-label text-slate-500">Mois</th>
                        <th className="text-right py-3 px-4 kpi-label text-slate-500">Revenus 2024</th>
                        <th className="text-right py-3 px-4 kpi-label text-slate-500">Revenus 2025</th>
                        <th className="text-center py-3 px-4 kpi-label text-slate-500">Var.</th>
                        <th className="text-right py-3 px-4 kpi-label text-slate-500">Profit 2024</th>
                        <th className="text-right py-3 px-4 kpi-label text-slate-500">Profit 2025</th>
                        <th className="text-center py-3 px-4 kpi-label text-slate-500">Var.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyComparison.slice(0, 13).map((m, idx) => (
                        <tr key={m.id || m._id || idx} className="table-row-hover border-b border-slate-100">
                          <td className="py-3 px-4 font-medium">{m.mois}</td>
                          <td className="py-3 px-4 text-right money-value">{formatCurrency(m.revenus_2024)}</td>
                          <td className="py-3 px-4 text-right money-value text-emerald-600">{formatCurrency(m.revenus_2025)}</td>
                          <td className="py-3 px-4 text-center"><ChangeIndicator current={m.revenus_2025} previous={m.revenus_2024} /></td>
                          <td className="py-3 px-4 text-right money-value">{formatCurrency(m.profit_2024)}</td>
                          <td className="py-3 px-4 text-right money-value text-emerald-600">{formatCurrency(m.profit_2025)}</td>
                          <td className="py-3 px-4 text-center"><ChangeIndicator current={m.profit_2025} previous={m.profit_2024} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* QoQ */}
        <TabsContent value="qoq">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Revenus Trimestriels - 2024 vs 2025</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={quarterlyComparison}>
                      <XAxis dataKey="trimestre" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v/1000}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="revenus_2024" name="2024" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="revenus_2025" name="2025" fill={CHART_COLORS.revenus} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {quarterlyComparison.map((q, idx) => (
                <Card key={q.id || q._id || idx}>
                  <CardContent className="p-5">
                    <p className="font-heading font-bold text-lg text-slate-900 mb-3">{q.trimestre}</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">2024</span>
                        <span className="money-value">{formatCurrency(q.revenus_2024)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">2025</span>
                        <span className="money-value text-emerald-600">{formatCurrency(q.revenus_2025)}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between items-center">
                        <span className="text-xs text-slate-400">Variation</span>
                        <ChangeIndicator current={q.revenus_2025} previous={q.revenus_2024} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* YoY */}
        <TabsContent value="yoy">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Comparaison Annuelle</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={annuelle}>
                      <XAxis dataKey="annee" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v/1000}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="revenus" name="Revenus" fill={CHART_COLORS.revenus} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="depenses" name="Dépenses" fill={CHART_COLORS.depenses} radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="profit" name="Profit" stroke={CHART_COLORS.profit} strokeWidth={3} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Résumé par Année</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 kpi-label text-slate-500">Année</th>
                        <th className="text-right py-3 px-4 kpi-label text-slate-500">Revenus</th>
                        <th className="text-center py-3 px-4 kpi-label text-slate-500">Var.</th>
                        <th className="text-right py-3 px-4 kpi-label text-slate-500">Dépenses</th>
                        <th className="text-right py-3 px-4 kpi-label text-slate-500">Profit</th>
                        <th className="text-center py-3 px-4 kpi-label text-slate-500">Var.</th>
                        <th className="text-right py-3 px-4 kpi-label text-slate-500">Marge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annuelle.map((y, idx) => {
                        const prev = annuelle[idx + 1];
                        const marge = y.revenus > 0 ? (y.profit / y.revenus * 100) : 0;
                        return (
                          <tr key={y.annee} className="table-row-hover border-b border-slate-100">
                            <td className="py-4 px-4 font-heading font-bold text-lg">{y.annee}</td>
                            <td className="py-4 px-4 text-right money-value text-emerald-600 font-medium">{formatCurrency(y.revenus)}</td>
                            <td className="py-4 px-4 text-center"><ChangeIndicator current={y.revenus} previous={prev?.revenus} /></td>
                            <td className="py-4 px-4 text-right money-value text-red-500">{formatCurrency(y.depenses)}</td>
                            <td className="py-4 px-4 text-right money-value font-medium">{formatCurrency(y.profit)}</td>
                            <td className="py-4 px-4 text-center"><ChangeIndicator current={y.profit} previous={prev?.profit} /></td>
                            <td className="py-4 px-4 text-right">
                              <span className={`px-2 py-1 rounded-full text-sm font-medium ${marge > 15 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {formatPercent(marge)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Comparaison;
